import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Row, Col, Form, InputGroup,
    Badge, Button, Modal, Spinner, Table
} from 'react-bootstrap';
import {
    Search, XCircleFill, Download, XCircle,
    ClockHistory, PrinterFill, ExclamationTriangleFill,
    CheckCircleFill, ClockFill, FileEarmarkText, FunnelFill
} from 'react-bootstrap-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Boletos.css';
import Navbar from '../../components/navbar/navbar';
import { formatDate } from '../../utils/formatDate';
import { parseISO } from 'date-fns';
import apinode from '../../services/apinode';
import ReactPaginate from 'react-paginate';

/* ── helpers ── */
function useDebounce(value, delay) {
    const [deb, setDeb] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDeb(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return deb;
}

const fmtMoeda = (v) =>
    (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtData = (d) =>
    d ? new Date(d).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—';

const SITUACAO_META = {
    'EM CARTEIRA':     { label: 'Em Carteira', bg: 'primary', icon: <ClockFill size={10} /> },
    'LIQUIDADO REDE':  { label: 'Pago',        bg: 'success', icon: <CheckCircleFill size={10} /> },
    'LIQUIDADO COMPE': { label: 'Pago',        bg: 'success', icon: <CheckCircleFill size={10} /> },
    'VENCIDO':         { label: 'Vencido',     bg: 'danger',  icon: <ExclamationTriangleFill size={10} /> },
};

function SituacaoBadge({ situacao }) {
    const m = SITUACAO_META[situacao] ?? { label: situacao, bg: 'secondary', icon: null };
    return (
        <Badge
            bg={m.bg}
            className="d-inline-flex align-items-center gap-1 px-2 py-1"
            style={{ fontSize: '0.7rem', letterSpacing: '.04em', fontWeight: 600 }}
        >
            {m.icon} {m.label}
        </Badge>
    );
}

const SITUACOES_FILTER = [
    { label: 'Todos',       value: '' },
    { label: 'Em Carteira', value: 'EM CARTEIRA' },
    { label: 'Pagos',       value: 'LIQUIDADO REDE' },
    { label: 'Vencidos',    value: 'VENCIDO' },
];

/* ── main component ── */
export default function Boletos() {
    const [boletos, setBoletos]         = useState([]);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState(null);
    const [searchTerm, setSearchTerm]   = useState('');
    const [filterSit, setFilterSit]     = useState('');
    const [perPage, setPerPage]         = useState(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [printingId, setPrintingId]   = useState(null);
    const [showModal, setShowModal]     = useState(false);
    const [seuNumero, setSeuNumero]     = useState('');
    const [dataVenc, setDataVenc]       = useState('');

    const debouncedSearch = useDebounce(searchTerm, 280);

    useEffect(() => {
        (async () => {
            try {
                const r = await apinode.get(`sicredi/boletos/${localStorage.idconta}`);
                setBoletos(r.data);
            } catch { setError('Erro ao carregar boletos'); }
            finally   { setLoading(false); }
        })();
    }, []);

    const filtered = useCallback(() => {
        const t = debouncedSearch.toLowerCase();
        return boletos.filter(b => {
            const sit = !filterSit
                || b.situacao === filterSit
                || (filterSit === 'LIQUIDADO REDE' && b.situacao === 'LIQUIDADO COMPE');
            const srch = !t
                || b.pes_razao?.toLowerCase().includes(t)
                || b.pes_cnpj_cpf?.includes(t)
                || b.nossonumero?.includes(t)
                || b.linhaDigitavel?.includes(t)
                || fmtMoeda(b.valor).includes(t);
            return sit && srch;
        });
    }, [boletos, debouncedSearch, filterSit])();

    useEffect(() => setCurrentPage(0), [debouncedSearch, filterSit]);

    const pageCount = Math.ceil(filtered.length / perPage);
    const paginated = filtered.slice(currentPage * perPage, currentPage * perPage + perPage);

    /* stats */
    const totalAberto  = boletos.filter(b => b.situacao === 'EM CARTEIRA').length;
    const totalPago    = boletos.filter(b => b.situacao?.startsWith('LIQUIDADO')).length;
    const totalVencido = boletos.filter(b => b.situacao === 'VENCIDO').length;
    const valorTotal   = filtered.reduce((s, b) => s + (b.valor ?? 0), 0);

    /* actions */
    const handleImprimir = async (linhaDigitavel, id) => {
        setPrintingId(id);
        try {
            const r = await apinode.get(`/sicredi/imprimir_bol_sicredi/${localStorage.idconta}`, {
                params: { linhaDigitavel }, responseType: 'blob',
            });
            window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
            toast.success('Boleto gerado!');
        } catch { toast.error('Erro ao gerar boleto.'); }
        finally { setPrintingId(null); }
    };

    const handleDownload = async (id) => {
        if (!window.confirm('Deseja baixar esta conta?')) return;
        try {
            const r = await apinode.put(`/contasreceber/pgconta/${localStorage.idconta}?ids=${id}`, {});
            toast.success(r.data.retorno);
        } catch (e) { toast.error(`Erro: ${e.message}`); }
    };

    const handleCancel = async (nossonumero) => {
        if (!window.confirm('Cancelar este boleto?')) return;
        try {
            await apinode.patch(`/sicredi/cancelar_bol_sicredi/${localStorage.idconta}/${nossonumero}`);
            toast.success('Boleto cancelado.');
            setBoletos(p => p.filter(b => b.nossonumero !== nossonumero));
        } catch (e) { toast.error('Erro ao cancelar: ' + (e.response?.data?.error ?? e.message)); }
    };

    const handleExtend = async () => {
        try {
            await apinode.patch(`/sicredi/atualizar_bol_sicredi/${localStorage.idconta}/${seuNumero}`, {
                dataVencimento: formatDate(parseISO(dataVenc), 'yyyy-MM-dd'), seuNumero,
            });
            const r = await apinode.get(`/sicredi/boletos/${localStorage.idconta}`);
            setBoletos(r.data);
            setShowModal(false);
            toast.success('Boleto prorrogado!');
        } catch { toast.error('Erro ao prorrogar.'); }
    };

    if (loading) return (
        <>
            <Navbar />
            <div className="bol-center-state">
                <Spinner animation="border" variant="primary" />
                <span className="ms-3 text-muted">Carregando boletos…</span>
            </div>
        </>
    );

    if (error) return (
        <>
            <Navbar />
            <div className="bol-center-state text-danger">{error}</div>
        </>
    );

    return (
        <>
            <Navbar />
            <div className="bol-root">
                <Container fluid="xl" className="py-4 px-3 px-md-4">

                    {/* PAGE HEADER */}
                    <div className="d-flex align-items-center mb-4">
                        <div className="bol-page-icon me-3">
                            <FileEarmarkText size={22} />
                        </div>
                        <div>
                            <h1 className="bol-page-title mb-0">Boletos</h1>
                            <p className="text-muted mb-0" style={{ fontSize: '.82rem' }}>
                                Gerencie seus boletos bancários
                            </p>
                        </div>
                    </div>

                    {/* STAT CARDS */}
                    <Row className="g-3 mb-4">
                        {[
                            { label: 'Total',       value: boletos.length, cls: 'stat-total'   },
                            { label: 'Em Carteira', value: totalAberto,    cls: 'stat-carteira'},
                            { label: 'Pagos',       value: totalPago,      cls: 'stat-pago'    },
                            { label: 'Vencidos',    value: totalVencido,   cls: 'stat-vencido' },
                        ].map(s => (
                            <Col xs={6} lg={3} key={s.label}>
                                <div className={`bol-stat ${s.cls}`}>
                                    <span className="bol-stat__value">{s.value}</span>
                                    <span className="bol-stat__label">{s.label}</span>
                                </div>
                            </Col>
                        ))}
                    </Row>

                    {/* TOOLBAR */}
                    <div className="bol-toolbar mb-3 p-3">
                        <Row className="g-2 align-items-center">
                            <Col xs={12} md={5}>
                                <InputGroup>
                                    <InputGroup.Text className="bg-white border-end-0 text-muted">
                                        <Search size={14} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        className="border-start-0 ps-1 shadow-none"
                                        placeholder="Nome, CPF, nosso número, linha digitável…"
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <Button
                                            variant="outline-secondary"
                                            className="border-start-0"
                                            onClick={() => setSearchTerm('')}
                                        >
                                            <XCircleFill size={13} />
                                        </Button>
                                    )}
                                </InputGroup>
                            </Col>

                            <Col xs={12} md={5}>
                                <div className="d-flex align-items-center gap-1 flex-wrap">
                                    <FunnelFill size={12} className="text-muted me-1" />
                                    {SITUACOES_FILTER.map(s => (
                                        <button
                                            key={s.value}
                                            className={`bol-chip ${filterSit === s.value ? 'bol-chip--active' : ''}`}
                                            onClick={() => setFilterSit(s.value)}
                                        >
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                            </Col>

                            <Col xs={12} md={2}>
                                <div className="d-flex align-items-center justify-content-md-end gap-2">
                                    <span className="text-muted" style={{ fontSize: '.78rem', whiteSpace: 'nowrap' }}>Por pág.:</span>
                                    <Form.Select
                                        size="sm"
                                        style={{ width: 68 }}
                                        value={perPage}
                                        onChange={e => { setPerPage(Number(e.target.value)); setCurrentPage(0); }}
                                    >
                                        {[5, 10, 15, 25].map(n => <option key={n} value={n}>{n}</option>)}
                                    </Form.Select>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {/* RESULTS BAR */}
                    <div className="d-flex align-items-center justify-content-between mb-2 px-1">
                        <span style={{ fontSize: '.8rem', color: '#888' }}>
                            {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
                            {(debouncedSearch || filterSit) && (
                                <button
                                    className="bol-clear-link ms-2"
                                    onClick={() => { setSearchTerm(''); setFilterSit(''); }}
                                >
                                    limpar filtros ×
                                </button>
                            )}
                        </span>
                        <span style={{ fontSize: '.82rem', fontWeight: 600 }}>
                            Total: <span className="text-primary">{fmtMoeda(valorTotal)}</span>
                        </span>
                    </div>

                    {/* TABLE */}
                    <div className="bol-table-wrap">
                        <Table responsive hover className="bol-table mb-0 align-middle">
                            <thead>
                                <tr>
                                    <th>Beneficiário</th>
                                    <th>CPF / CNPJ</th>
                                    <th className="text-end">Valor</th>
                                    <th className="text-center">Vencimento</th>
                                    <th>Nosso Nº</th>
                                    <th className="text-center">Situação</th>
                                    <th className="text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.length === 0 ? (
                                    <tr>
                                        <td colSpan={7}>
                                            <div className="bol-empty">
                                                <FileEarmarkText size={40} className="mb-2" />
                                                <p className="mb-0">Nenhum boleto encontrado</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : paginated.map(b => {
                                    const isVencido = b.situacao === 'VENCIDO';
                                    const isPago = b.situacao === 'LIQUIDADO REDE' || b.situacao === 'LIQUIDADO COMPE';
                                    return (
                                        <tr
                                            key={b.id}
                                            className={isVencido ? 'tr-vencido' : isPago ? 'tr-pago' : ''}
                                        >
                                            <td>
                                                <span className="bol-nome">{b.pes_razao}</span>
                                            </td>
                                            <td>
                                                <code className="bol-code">{b.pes_cnpj_cpf}</code>
                                            </td>
                                            <td className="text-end">
                                                <span className="bol-valor">{fmtMoeda(b.valor)}</span>
                                            </td>
                                            <td className="text-center">
                                                <span style={{ fontSize: '.84rem' }}>{fmtData(b.dataVencimento)}</span>
                                            </td>
                                            <td>
                                                <code className="bol-code">{b.nossonumero}</code>
                                            </td>
                                            <td className="text-center">
                                                <SituacaoBadge situacao={b.situacao} />
                                            </td>
                                            <td>
                                                <div className="d-flex gap-1 justify-content-center">
                                                    <Button
                                                        size="sm" variant="outline-primary"
                                                        title="Imprimir"
                                                        className="bol-btn"
                                                        disabled={printingId === b.id}
                                                        onClick={() => handleImprimir(b.linhaDigitavel, b.id)}
                                                    >
                                                        {printingId === b.id
                                                            ? <Spinner size="sm" animation="border" />
                                                            : <PrinterFill size={13} />}
                                                    </Button>
                                                    <Button
                                                        size="sm" variant="outline-success"
                                                        title="Baixar conta"
                                                        className="bol-btn"
                                                        onClick={() => handleDownload(b.id)}
                                                    >
                                                        <Download size={13} />
                                                    </Button>
                                                    <Button
                                                        size="sm" variant="outline-warning"
                                                        title="Prorrogar"
                                                        className="bol-btn"
                                                        onClick={() => { setSeuNumero(b.nossonumero); setDataVenc(''); setShowModal(true); }}
                                                    >
                                                        <ClockHistory size={13} />
                                                    </Button>
                                                    <Button
                                                        size="sm" variant="outline-danger"
                                                        title="Cancelar"
                                                        className="bol-btn"
                                                        onClick={() => handleCancel(b.nossonumero)}
                                                    >
                                                        <XCircle size={13} />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>

                    {/* PAGINATION */}
                    {pageCount > 1 && (
                        <div className="d-flex justify-content-center mt-4">
                            <ReactPaginate
                                previousLabel="← Anterior"
                                nextLabel="Próximo →"
                                pageCount={pageCount}
                                onPageChange={({ selected }) => setCurrentPage(selected)}
                                containerClassName="pagination mb-0"
                                pageClassName="page-item"
                                pageLinkClassName="page-link"
                                previousClassName="page-item"
                                previousLinkClassName="page-link"
                                nextClassName="page-item"
                                nextLinkClassName="page-link"
                                disabledClassName="disabled"
                                activeClassName="active"
                                forcePage={currentPage}
                            />
                        </div>
                    )}
                </Container>
            </div>

            {/* MODAL PRORROGAR */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-6">
                        <ClockHistory className="me-2 text-warning" size={18} />
                        Prorrogar Boleto
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold small">Nova Data de Vencimento</Form.Label>
                            <Form.Control
                                type="date"
                                value={dataVenc}
                                onChange={e => setDataVenc(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label className="fw-semibold small">Nosso Número</Form.Label>
                            <Form.Control
                                type="text"
                                value={seuNumero}
                                onChange={e => setSeuNumero(e.target.value)}
                                required
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={() => setShowModal(false)}>Fechar</Button>
                    <Button variant="warning" className="text-white fw-semibold" onClick={handleExtend}>
                        Prorrogar
                    </Button>
                </Modal.Footer>
            </Modal>

            <ToastContainer position="top-right" autoClose={3000} />
        </>
    );
}